import React, { useCallback, useEffect, useRef, useState } from "react";
import "./recorder.css";
import baseUrlNew from "../BaseUrl/index";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useHistory, useLocation } from "react-router-dom";
import {
  HiOutlineMicrophone,
  HiOutlineStop,
  HiOutlineTrash,
  HiOutlineCheck,
} from "react-icons/hi";
import InspectionShell from "../components/InspectionShell";

function formatDuration(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${String(m).padStart(2, "0")}:${String(rem).padStart(2, "0")}`;
}

function pickMimeType() {
  if (typeof MediaRecorder === "undefined") return "";
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/ogg;codecs=opus",
  ];
  return candidates.find((t) => MediaRecorder.isTypeSupported(t)) || "";
}

function Recoder() {
  const history = useHistory();
  const location = useLocation();
  const id = location.state?.id;
  const carName = location.state?.car_name || "Vehicle";

  const [desvalue, setdesvalue] = useState("");
  const [saving, setSaving] = useState(false);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [error, setError] = useState("");

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioUrlRef = useRef(null);

  const clearTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const revokeAudioUrl = useCallback(() => {
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
  }, []);

  const resetRecording = useCallback(() => {
    clearTimer();
    if (mediaRecorderRef.current?.state === "recording") {
      try {
        mediaRecorderRef.current.stop();
      } catch (_) {
        /* ignore */
      }
    }
    mediaRecorderRef.current = null;
    stopStream();
    chunksRef.current = [];
    revokeAudioUrl();
    setAudioUrl(null);
    setAudioBlob(null);
    setRecording(false);
    setElapsed(0);
    setError("");
  }, [revokeAudioUrl]);

  useEffect(() => () => resetRecording(), [resetRecording]);

  const goBack = () => {
    history.push({
      pathname: "/inspection/carsteps",
      state: {
        id,
        car_name: carName,
        inspect: location.state?.inspect,
        report: location.state?.report,
      },
    });
  };

  const startRecording = async () => {
    setError("");
    if (!navigator.mediaDevices?.getUserMedia) {
      setError("Microphone access is not supported in this browser.");
      toast.error("Microphone not supported.");
      return;
    }
    if (typeof MediaRecorder === "undefined") {
      setError("Audio recording is not supported in this browser.");
      toast.error("Recording not supported.");
      return;
    }

    try {
      revokeAudioUrl();
      setAudioUrl(null);
      setAudioBlob(null);
      chunksRef.current = [];

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = pickMimeType();
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        clearTimer();
        stopStream();
        const type = recorder.mimeType || mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        chunksRef.current = [];
        const url = URL.createObjectURL(blob);
        audioUrlRef.current = url;
        setAudioBlob(blob);
        setAudioUrl(url);
        setRecording(false);
      };

      recorder.start(250);
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => {
        setElapsed((n) => n + 1);
      }, 1000);
    } catch (err) {
      stopStream();
      const message =
        err?.name === "NotAllowedError"
          ? "Microphone permission denied. Allow access and try again."
          : err?.message || "Could not start recording.";
      setError(message);
      toast.error(message);
    }
  };

  const stopRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state === "recording") {
      recorder.stop();
    } else {
      clearTimer();
      stopStream();
      setRecording(false);
    }
  };

  const uploadfile = (data) =>
    axios.post(`${baseUrlNew.baseUrl}api/v1/product_audio/`, data);

  const handleSave = async () => {
    if (!desvalue.trim()) {
      toast("Please write a description.");
      return;
    }
    if (!audioBlob) {
      toast("Please record audio before saving.");
      return;
    }

    setSaving(true);
    try {
      const ext = audioBlob.type.includes("mp4")
        ? "m4a"
        : audioBlob.type.includes("ogg")
          ? "ogg"
          : "webm";
      const fileName = `${Math.random().toString(36).substring(6)}_engine.${ext}`;
      const fileObject = new File([audioBlob], fileName, {
        type: audioBlob.type || "audio/webm",
      });
      const formData = new FormData();
      formData.append("audio", fileObject);
      formData.append("evaluate_audio", id);
      formData.append("description", desvalue.trim());
      await uploadfile(formData);
      toast.success("Audio has been saved.");
      goBack();
    } catch (err) {
      toast.error(err?.message || "Could not save audio. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const statusLabel = recording
    ? "Recording…"
    : audioUrl
      ? "Ready to save"
      : "Ready to record";

  return (
    <InspectionShell
      badge="Audio record"
      title={carName}
      onBack={goBack}
      mainClassName="pi-inspection-main--dashboard"
      className="pi-inspection-page--app"
      showFooter
    >
      <div className="pi-audio">
        <header className="pi-audio__hero">
          <div>
            <p className="pi-inspection-label">Media step</p>
            <h1>Record engine audio</h1>
            <p>
              Capture a short engine or cabin clip, add a note, then save it to
              this inspection.
            </p>
          </div>
          <div className="pi-audio__hero-icon" aria-hidden>
            <HiOutlineMicrophone size={26} />
          </div>
        </header>

        <section className="pi-audio__panel pi-audio__panel--recorder">
          <div className="pi-audio__panel-head">
            <h2>Recorder</h2>
            <p>Tap record, then stop when finished. You can replay before saving.</p>
          </div>

          <div className="pi-audio__stage">
            <div
              className={`pi-audio__pulse ${recording ? "is-live" : ""}`}
              aria-hidden
            >
              <span />
              <span />
              <HiOutlineMicrophone size={32} />
            </div>

            <div className="pi-audio__meta">
              <span
                className={`pi-audio__status ${recording ? "is-live" : ""}`}
              >
                {statusLabel}
              </span>
              <strong className="pi-audio__timer">{formatDuration(elapsed)}</strong>
            </div>

            {error ? <p className="pi-audio__error">{error}</p> : null}

            {audioUrl && !recording ? (
              <audio className="pi-audio__player" controls src={audioUrl} />
            ) : null}

            <div className="pi-audio__actions">
              {!recording ? (
                <button
                  type="button"
                  className="pi-audio__btn pi-audio__btn--primary"
                  onClick={startRecording}
                  disabled={saving}
                >
                  <HiOutlineMicrophone size={18} aria-hidden />
                  {audioUrl ? "Re-record" : "Record"}
                </button>
              ) : (
                <button
                  type="button"
                  className="pi-audio__btn pi-audio__btn--danger"
                  onClick={stopRecording}
                >
                  <HiOutlineStop size={18} aria-hidden />
                  Stop
                </button>
              )}

              {audioUrl && !recording ? (
                <button
                  type="button"
                  className="pi-audio__btn pi-audio__btn--ghost"
                  onClick={resetRecording}
                  disabled={saving}
                >
                  <HiOutlineTrash size={18} aria-hidden />
                  Clear
                </button>
              ) : null}
            </div>
          </div>
        </section>

        <section className="pi-audio__panel">
          <div className="pi-audio__panel-head">
            <h2>Description</h2>
            <p>Required — describe what this recording captures.</p>
          </div>
          <label className="pi-audio__field">
            <span className="pi-audio__field-label">Notes</span>
            <textarea
              className="pi-audio__textarea"
              value={desvalue}
              onChange={(e) => setdesvalue(e.target.value)}
              placeholder="e.g. Cold start idle, slight knock from front-right…"
              rows={5}
            />
          </label>

          <button
            type="button"
            className="pi-audio__btn pi-audio__btn--primary pi-audio__btn--save"
            onClick={handleSave}
            disabled={
              saving || recording || !audioBlob || !desvalue.trim()
            }
          >
            <HiOutlineCheck size={18} aria-hidden />
            {saving ? "Saving…" : "Save audio"}
          </button>
        </section>
      </div>

      <ToastContainer />
    </InspectionShell>
  );
}

export default Recoder;
